/**
 * Meal Timing Constants and Utilities
 * Defines when meals are typically eaten and when to show rating UI
 */

import { MealTypeKey } from './uiConstants'

/**
 * Meal type display order (for sorting meals)
 * breakfast → lunch → dinner → snacks → dessert
 */
export const MEAL_TYPE_ORDER: Record<string, number> = {
  breakfast: 0,
  lunch: 1,
  dinner: 2,
  snacks: 3,
  dessert: 4
}

/**
 * Meal time windows (in 24-hour format)
 * Defines the typical time range when each meal is eaten
 */
export const MEAL_TIME_WINDOWS = {
  breakfast: {
    start: 6,  // 6:00 AM
    end: 11,   // 11:00 AM
    ratingAfter: 11 // Show rating after 11:00 AM
  },
  lunch: {
    start: 11, // 11:00 AM
    end: 15,   // 3:00 PM
    ratingAfter: 15 // Show rating after 3:00 PM
  },
  dinner: {
    start: 17, // 5:00 PM
    end: 21,   // 9:00 PM
    ratingAfter: 21 // Show rating after 9:00 PM
  },
  snacks: {
    start: 14, // 2:00 PM
    end: 22,   // 10:00 PM
    ratingAfter: 22 // Show rating after 10:00 PM
  },
  dessert: {
    start: 19, // 7:00 PM
    end: 23,   // 11:00 PM
    ratingAfter: 23 // Show rating after 11:00 PM
  }
} as const

/**
 * Meal time status types
 */
export type MealTimeStatus = 'upcoming' | 'current' | 'past'

/**
 * Check if rating UI should be shown for a meal
 * @param mealType - The type of meal (breakfast, lunch, dinner, etc.)
 * @param currentTime - Current time (defaults to now)
 * @returns true if rating should be shown
 */
export function shouldShowRating(
  mealType: MealTypeKey,
  currentTime: Date = new Date()
): boolean {
  const hour = currentTime.getHours()
  const window = MEAL_TIME_WINDOWS[mealType]

  if (!window) return false

  return hour >= window.ratingAfter
}

/**
 * Get the current status of a meal time window
 * @param mealType - The type of meal
 * @param currentTime - Current time (defaults to now)
 * @returns 'upcoming' | 'current' | 'past'
 */
export function getMealTimeStatus(
  mealType: MealTypeKey,
  currentTime: Date = new Date()
): MealTimeStatus {
  const hour = currentTime.getHours()
  const window = MEAL_TIME_WINDOWS[mealType]

  if (!window) return 'upcoming'

  if (hour < window.start) {
    return 'upcoming'
  } else if (hour >= window.start && hour < window.end) {
    return 'current'
  } else {
    return 'past'
  }
}

/**
 * Get a human-readable time label for a meal
 * @param mealType - The type of meal
 * @returns Formatted time range string
 */
export function getMealTimeLabel(mealType: MealTypeKey): string {
  const window = MEAL_TIME_WINDOWS[mealType]

  if (!window) return ''

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM'
    if (hour < 12) return `${hour} AM`
    if (hour === 12) return '12 PM'
    return `${hour - 12} PM`
  }

  return `${formatHour(window.start)} - ${formatHour(window.end)}`
}

/**
 * Get the next upcoming meal based on current time
 * @param enabledMealTypes - Array of meal types that are enabled
 * @param currentTime - Current time (defaults to now)
 * @returns The next meal type or null
 */
export function getNextMealType(
  enabledMealTypes: MealTypeKey[],
  currentTime: Date = new Date()
): MealTypeKey | null {
  const hour = currentTime.getHours()

  // Sort meal types by start time
  const sortedMealTypes = enabledMealTypes
    .filter(type => MEAL_TIME_WINDOWS[type])
    .sort((a, b) => {
      const windowA = MEAL_TIME_WINDOWS[a]
      const windowB = MEAL_TIME_WINDOWS[b]
      return windowA.start - windowB.start
    })

  // Find first meal that hasn't ended yet
  for (const mealType of sortedMealTypes) {
    const window = MEAL_TIME_WINDOWS[mealType]
    if (hour < window.end) {
      return mealType
    }
  }

  // If all meals have passed, return the first meal (for tomorrow)
  return sortedMealTypes[0] || null
}
